import { Link } from 'react-router-dom';
import {
  SF_CA_NOTICE,
  SF_DO_NOT_SELL,
  SF_PRIVACY,
  SF_TERMS,
} from '../lib/externalLinks';
import { useEventState } from '../lib/eventState';

// IMT's corporate privacy moved to Complianz region-specific statements
// (2026-07). Supersedes the old /privacy/ page. BOTH query params are
// load-bearing: cmplz_region_redirect=true lets Complianz redirect the
// visitor to their own region's statement (e.g. EU visitors land on the EU
// notice), with cmplz-region=us as the US-audience default. Don't strip them.
const IMT_PRIVACY =
  'https://www.infinitymarketing.com/privacy-statement-us/?cmplz_region_redirect=true&cmplz-region=us';

// The staging notice renders only in non-indexable (staging / dev) builds.
// VITE_ALLOW_INDEXING=true — the production posture — makes this statically
// false and the bundler drops the block, the same build-time gate seo.ts
// uses for setCanonical().
const IS_INDEXABLE_BUILD = import.meta.env.VITE_ALLOW_INDEXING === 'true';

/**
 * Site footer — a single full-width red linear-gradient panel (vertical:
 * #c71635 → #f4002b) containing every footer surface:
 *   1. Staging environment notice (amber-on-dark inset for contrast) —
 *      non-indexable (staging/dev) builds only.
 *   2. State Farm legal block — slogan, eligibility / family-of-companies
 *      disclosures, mashed-up legal-link nav, and the white SF logo
 *      (top-right on md+, falls back to inline on mobile).
 *   3. Operator disclosure + copyright (IMT on behalf of State Farm).
 *
 * Inner content is clamped to max-w-[1440px] with the same responsive side
 * padding as <AppBar>, so the gradient bleeds edge-to-edge while the text
 * column lines up with the header above it.
 */
export default function SiteFooter() {
  // Contest-end / Free Play (GHS5-85): the Official Rules link leaves the footer
  // per direction (the /rules route stays reachable by URL for legal retention;
  // Free Play conditions are still linked from the registration + play-gate
  // consent surfaces). Legal to confirm the rules no longer need to be posted.
  const { contestEnded } = useEventState();
  return (
    <footer
      role="contentinfo"
      className="bg-[linear-gradient(to_bottom,#c71635_0%,#f4002b_100%)] text-white"
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12">
        <div className="flex flex-col gap-6">
          {!IS_INDEXABLE_BUILD && (
            <p
              role="note"
              className="mx-auto w-full max-w-3xl rounded-md border border-amber-300/40 bg-black/30 px-4 py-2 text-center text-xs font-medium uppercase tracking-wider text-amber-100"
            >
              Staging environment: This site is a temporary build for privacy-mode
              review. Designs, copy, and functionality are still being finalized and
              do not represent the final State Farm Gamerhood Season 5 experience.
              <span className="mt-1 block normal-case tracking-normal text-amber-100/80">
                For this build: search-engine indexing, sitemap, and canonical URL
                are intentionally disabled.
              </span>
            </p>
          )}

          <div className="md:relative">
            <div className="space-y-5 md:max-w-[640px]">
              <p className="text-sm font-bold sm:text-base">
                Like a good neighbor, State Farm is there.<sup className="ml-[0.04em] text-[0.6em] top-[-0.5em]">&reg;</sup>
              </p>

              <hr className="border-t border-white/40" aria-hidden="true" />

              <p className="text-xs leading-relaxed sm:text-sm">
                Eligibility, availability and other aspects of products or
                services of the State Farm Family of Companies are subject to
                restrictions and may vary by state.
              </p>

              <p className="text-xs sm:text-sm">
                State Farm Mutual Automobile Insurance Company
              </p>

              <ul className="space-y-1 text-xs sm:text-sm">
                <li>State Farm Indemnity Company Bloomington, IL</li>
                <li>
                  State Farm County Mutual Insurance Company of Texas
                  Richardson, TX
                </li>
                <li>State Farm Fire and Casualty Company</li>
                <li>State Farm General Insurance Company Bloomington, IL</li>
                <li>State Farm Florida Insurance Company Tallahassee, FL</li>
              </ul>

              <nav
                aria-label="Site and State Farm legal"
                className="flex flex-wrap gap-x-5 gap-y-2 pt-2 text-xs sm:text-sm"
              >
                {!contestEnded && (
                  <Link to="/rules" className="underline hover:no-underline">
                    Official Rules
                  </Link>
                )}
                {/* Site-specific notices for THIS site. Both the SF corporate
                    notice and the IMT corporate notice scope themselves to
                    their own .com properties; the notices below are the
                    site-of-collection authority. See
                    docs/privacy-terms-justification.md. */}
                <Link to="/privacy" className="underline hover:no-underline">
                  Privacy Notice
                </Link>
                <Link to="/terms" className="underline hover:no-underline">
                  Terms of Use
                </Link>
                <Link to="/cookies" className="underline hover:no-underline">
                  Cookie Policy
                </Link>
                <Link
                  to="/privacy-settings"
                  className="underline hover:no-underline"
                >
                  Privacy Settings
                </Link>
                <a
                  href={SF_PRIVACY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  State Farm Privacy
                </a>
                <a
                  href={SF_TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  State Farm Terms
                </a>
                <a
                  href={IMT_PRIVACY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  IMT Privacy Policy
                </a>
                <a
                  href={SF_CA_NOTICE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  CA Notice at Collection
                </a>
                {/* Same-tab on purpose — CCPA right-to-opt-out flows must
                    remain easily accessible and not buried in tab management. */}
                <a href={SF_DO_NOT_SELL} className="underline hover:no-underline">
                  Do Not Sell or Share My Personal Information
                </a>
              </nav>
            </div>

            {/*
              Logo sits as a SIBLING of the content column (not a child of
              space-y-5) so it doesn't inherit the cascading margin-top. On
              md+ it absolutely positions to the top-right of the relative
              parent; on mobile it flows directly under the content column.
            */}
            <a
              href="https://www.statefarm.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="State Farm (opens in a new tab)"
              className="mt-5 block w-[150px] md:absolute md:right-0 md:top-0 md:mt-0"
            >
              <img
                src="/assets/sf-logos/StateFarm_w.svg"
                alt="State Farm"
                width={150}
                height={22}
                className="block w-[150px]"
              />
            </a>
          </div>

          {/* WCAG AA: the gradient bottoms out at #f4002b, where even pure
              white only reaches 4.30:1 — under the 4.5:1 minimum for this
              text-xs copy. The black/20 inset (same treatment family as the
              staging notice above) lifts white to 5.7:1 and white/90 to 5.2:1. */}
          <div className="mx-auto w-full max-w-3xl space-y-2 rounded-md bg-black/20 px-4 py-3 text-center text-xs text-white">
            <p>
              Operated by Infinity Marketing Team, LLC on behalf of State Farm
              Mutual Automobile Insurance Company.
            </p>
            <p className="text-white/90">
              &copy; {new Date().getFullYear()} State Farm Mutual Automobile
              Insurance Company. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
